import React, { useState, useEffect, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, LineElement, PointElement } from 'chart.js';
import { FormControl, Select, MenuItem, Button } from '@mui/material';
import ProductSelector from './ProductSelector';
import SwitchingReasonsHistogram from './SwitchingReasonsHistogram';
import { apiPost } from '../services/apiClient';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, LineElement, PointElement);

const TIME_RANGE_OPTIONS = [
    { value: '3W', label: '3 Weeks' },
    { value: '1M', label: '1 Month' },
    { value: '3M', label: '3 Months' },
    { value: '6M', label: '6 Months' },
    { value: '1Y', label: '1 Year' }
];

const DailyLeaverJoiner = ({ topicMap, companyName, initialTimeRange,
    // Optional controlled product selection, so a parent (e.g. the Daily
    // Trackers tab switcher) can keep the selected topic sticky across
    // different chart types. Falls back to internal state when omitted.
    selectedProduct: controlledSelectedProduct, onProductChange }) => {
    const [internalSelectedProduct, setInternalSelectedProduct] = useState('');
    const isProductControlled = controlledSelectedProduct !== undefined && typeof onProductChange === 'function';
    const selectedProduct = isProductControlled ? controlledSelectedProduct : internalSelectedProduct;
    const setSelectedProduct = isProductControlled ? onProductChange : setInternalSelectedProduct;
    // `initialTimeRange` only seeds the starting value (used by the Report
    // Generator to capture a longer window); the live selector still works.
    const [timeRange, setTimeRange] = useState(initialTimeRange || '3W');
    const [chartData, setChartData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDateRange, setSelectedDateRange] = useState(null);
    const [showReasons, setShowReasons] = useState(false);
    const [isHeaderHovered, setIsHeaderHovered] = useState(false);

    // Use all available topics since we'll fetch data directly from Supabase
    const filteredTopicMap = useMemo(() => {
        if (!topicMap) return {};

        // Auto-select first available product if none selected
        if (Object.keys(topicMap).length > 0 && !selectedProduct) {
            const sortedTopics = Object.values(topicMap).sort((a, b) => {
                const priorityA = a.priority === undefined ? Infinity : a.priority;
                const priorityB = b.priority === undefined ? Infinity : b.priority;
                return priorityA - priorityB;
            });
            
            if (sortedTopics[0]) {
                setSelectedProduct(sortedTopics[0].id);
            }
        }
        
        return topicMap;
    }, [topicMap, selectedProduct]);

    const getSelectedProductName = () => {
        if (selectedProduct && topicMap && topicMap[selectedProduct]) {
            return topicMap[selectedProduct].name;
        }
        return 'Product';
    };

    const getTimeRangeLabel = () => {
        const option = TIME_RANGE_OPTIONS.find(o => o.value === timeRange);
        return option ? option.label : timeRange;
    };

    const buildMainTitle = () => {
        return `${getSelectedProductName()} · Net Adds and Losses`;
    };

    const buildSubline = () => {
        return `${getTimeRangeLabel()} • Daily View`;
    };

    // Fetch data from Supabase when product or time range changes
    useEffect(() => {
        if (!selectedProduct) {
            setChartData(null);
            return;
        }

        const fetchData = async () => {
            setIsLoading(true);

            try {
                // Calculate date range - use current date as end
                const now = new Date();
                const futureDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Today only, no future dates
                let startDate;

                switch (timeRange) {
                    case '3W':
                        startDate = new Date(futureDate.getTime() - (3 * 7 * 24 * 60 * 60 * 1000));
                        break;
                    case '1M':
                        startDate = new Date(futureDate.getTime() - (30 * 24 * 60 * 60 * 1000));
                        break;
                    case '3M':
                        startDate = new Date(futureDate.getTime() - (90 * 24 * 60 * 60 * 1000));
                        break;
                    case '6M':
                        startDate = new Date(futureDate.getTime() - (180 * 24 * 60 * 60 * 1000));
                        break;
                    case '1Y':
                        startDate = new Date(futureDate.getFullYear() - 1, futureDate.getMonth(), futureDate.getDate());
                        break;
                    default:
                        startDate = new Date(futureDate.getTime() - (3 * 7 * 24 * 60 * 60 * 1000));
                }

                // Convert to Unix timestamps
                const startTime = Math.floor(startDate.getTime() / 1000);
                const endTime = Math.floor(futureDate.getTime() / 1000);

                console.log('Fetching leaver-joiner data for:', {
                    topicId: selectedProduct,
                    timeRange,
                    startTime,
                    endTime
                });

                // Call backend instead of Supabase directly
                const response = await apiPost('topic/leaver-joiner/daily', {
                    topic_id: selectedProduct,
                    start_time: startTime,
                    end_time: endTime,
                });

                if (!response.ok) {
                    throw new Error(`Backend error: ${response.status}`);
                }

                const json = await response.json();
                const data = json.data ?? json ?? [];

                console.log('Received data:', data?.length || 0, 'days');

                // Create complete date range with 0 values for missing days
                const allDates = [];
                const startDateUTC = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                const endDateUTC = new Date(futureDate.getFullYear(), futureDate.getMonth(), futureDate.getDate());
                
                console.log('Generating date range from:', startDateUTC.toISOString(), 'to:', endDateUTC.toISOString());
                
                // Generate dates more reliably
                const currentDate = new Date(startDateUTC);
                while (currentDate <= endDateUTC) {
                    allDates.push(new Date(currentDate));
                    currentDate.setDate(currentDate.getDate() + 1);
                }
                const totalDays = allDates.length;
                console.log('Expected total days:', totalDays);
                
                console.log('Generated', allDates.length, 'total dates in range');
                console.log('All dates generated:', allDates.map(d => d.toDateString()));
                
                // Check for duplicates
                const dateStrings = allDates.map(d => d.toDateString());
                const duplicates = dateStrings.filter((item, index) => dateStrings.indexOf(item) !== index);
                if (duplicates.length > 0) {
                    console.log('DUPLICATE DATES FOUND:', duplicates);
                }

                // Create data map from backend response
                const dataMap = {};
                if (data && data.length > 0) {
                    data.forEach(row => {
                        const dateKey = new Date(row.date).toDateString();
                        dataMap[dateKey] = row;
                    });
                    console.log('Backend provided data for dates:', Object.keys(dataMap));
                }

                // Fill in missing days with zero values
                const completeData = allDates.map(date => {
                    const dateKey = date.toDateString();
                    const existingData = dataMap[dateKey];
                    
                    if (!existingData) {
                        console.log('Filling missing date with zeros:', dateKey);
                    }
                    
                    return existingData || {
                        date: date.toISOString().split('T')[0], // YYYY-MM-DD format
                        strong_joiner_count: 0,
                        possible_joiner_count: 0,
                        strong_leaver_count: 0,
                        possible_leaver_count: 0
                    };
                });

                console.log('Complete data with zeros:', completeData.length, 'days (including missing days)');
                console.log('Sample complete data:', completeData.slice(0, 3));

                // Process the complete data for Chart.js - always use daily data
                const sortedData = completeData.sort((a, b) => new Date(a.date) - new Date(b.date));

                const labels = sortedData.map(row => {
                    const date = new Date(row.date + 'T00:00:00.000Z');
                    return date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        timeZone: 'UTC'
                    });
                });
                
                console.log('Chart labels:', labels);
                
                // Check for duplicate labels
                const duplicateLabels = labels.filter((item, index) => labels.indexOf(item) !== index);
                if (duplicateLabels.length > 0) {
                    console.log('DUPLICATE LABELS FOUND:', duplicateLabels);
                }
                
                // Show missing days
                console.log('Expected vs actual labels:');
                console.log('Sorted data dates:', sortedData.map(row => new Date(row.date).toDateString()));
                console.log('Sorted data labels:', labels);

                const strongJoinersData = sortedData.map(row => row.strong_joiner_count);
                const possibleJoinersData = sortedData.map(row => row.possible_joiner_count);
                const possibleLeaversData = sortedData.map(row => -row.possible_leaver_count); // Negative for below zero
                const strongLeaversData = sortedData.map(row => -row.strong_leaver_count); // Negative for below zero
                

                setChartData({
                    labels,
                    datasets: [
                        {
                            type: 'bar',
                            label: 'Strong Joiners',
                            data: strongJoinersData,
                            backgroundColor: 'rgba(0, 255, 0, 0.7)',
                            borderColor: 'rgba(0, 255, 0, 1)',
                            borderWidth: 1,
                            stack: 'Stack 0',
                            skipNull: false,
                            spanGaps: false
                        },
                        {
                            type: 'bar',
                            label: 'Possible Joiners',
                            data: possibleJoinersData,
                            backgroundColor: 'rgba(144, 238, 144, 0.7)',
                            borderColor: 'rgba(144, 238, 144, 1)',
                            borderWidth: 1,
                            stack: 'Stack 0'
                        },
                        {
                            type: 'bar',
                            label: 'Strong Leavers',
                            data: strongLeaversData,
                            backgroundColor: 'rgba(255, 0, 0, 0.7)',
                            borderColor: 'rgba(255, 0, 0, 1)',
                            borderWidth: 1,
                            stack: 'Stack 0'
                        },
                        {
                            type: 'bar',
                            label: 'Possible Leavers',
                            data: possibleLeaversData,
                            backgroundColor: 'rgba(255, 99, 71, 0.7)',
                            borderColor: 'rgba(255, 99, 71, 1)',
                            borderWidth: 1,
                            stack: 'Stack 0'
                        },
                        
                    ]
                });
                
            } catch (error) {
                console.error('Error fetching leaver-joiner data:', error);
                setChartData(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [selectedProduct, timeRange]);

    // Set date range for switching reasons when timeRange or selectedProduct changes
    useEffect(() => {
        if (selectedProduct && timeRange) {
            const now = new Date();
            const futureDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Today only
            let startDate;
            
            switch (timeRange) {
                case '3W':
                    startDate = new Date(futureDate.getTime() - (3 * 7 * 24 * 60 * 60 * 1000));
                    break;
                case '1M':
                    startDate = new Date(futureDate.getTime() - (30 * 24 * 60 * 60 * 1000));
                    break;
                case '3M':
                    startDate = new Date(futureDate.getTime() - (90 * 24 * 60 * 60 * 1000));
                    break;
                case '6M':
                    startDate = new Date(futureDate.getTime() - (180 * 24 * 60 * 60 * 1000));
                    break;
                case '1Y':
                    startDate = new Date(futureDate.getFullYear() - 1, futureDate.getMonth(), futureDate.getDate());
                    break;
                default:
                    startDate = new Date(futureDate.getTime() - (3 * 7 * 24 * 60 * 60 * 1000));
            }
            
            setSelectedDateRange({ start: startDate, end: futureDate });
        } else {
            setSelectedDateRange(null);
        }
    }, [selectedProduct, timeRange]);

    if (!topicMap || Object.keys(filteredTopicMap).length === 0) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                No leaver-joiner data available
            </div>
        );
    }

    // Consistent control height for all dropdowns
    const CONTROL_HEIGHT = 36;
    const CONTROL_GAP = 12;

    const selectStyle = {
        width: '100%',
        height: CONTROL_HEIGHT,
        '& .MuiSelect-select': {
            paddingTop: '6px',
            paddingBottom: '6px',
            paddingRight: '24px !important',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d0d0d0',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#999',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1976d2',
        }
    };

    const filterLabelStyle = {
        fontSize: '9px',
        fontWeight: 600,
        color: '#999',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '3px'
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                left: 0,
                right: 0,
                top: 0,
                bottom: 0
            }
        },
        scales: {
            x: {
                stacked: true,
                title: {
                    display: false
                },
                type: 'category',
                display: true,
                offset: false,
                bounds: 'data',
                grid: {
                    offset: false,
                    display: true,
                    drawOnChartArea: true,
                    color: 'rgba(0, 0, 0, 0.1)',
                    lineWidth: 1
                },
                ticks: {
                    maxRotation: 0,
                    minRotation: 0,
                    autoSkip: true,
                    maxTicksLimit: 12,
                    callback: function(value, index, ticks) {
                        return this.getLabelForValue(value);
                    }
                },
                categoryPercentage: 0.98,
                barPercentage: 1.0
            },
            y: {
                stacked: true,
                title: {
                    display: true,
                    text: 'Count'
                },
                grid: {
                    color: (context) => {
                        if (context.tick.value === 0) {
                            return 'rgba(0, 0, 0, 0.8)'; // Bold line at zero
                        }
                        return 'rgba(0, 0, 0, 0.1)'; // Light grid lines
                    },
                    lineWidth: (context) => {
                        if (context.tick.value === 0) {
                            return 2; // Thicker line at zero
                        }
                        return 1;
                    }
                }
            }
        },
        plugins: {
            legend: {
                display: true,
                position: 'bottom'
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    title: function(context) {
                        return context[0].label;
                    },
                                         label: function(context) {
                         const datasetLabel = context.dataset.label;
                         const value = context.parsed.y;
                         
                         if (datasetLabel === 'Strong Leavers' || datasetLabel === 'Possible Leavers') {
                             return `${datasetLabel}: ${Math.abs(value)}`;
                         } else if (datasetLabel === 'Net') {
                             return `${datasetLabel}: ${value >= 0 ? '+' : ''}${value}`;
                         } else {
                             return `${datasetLabel}: ${value}`;
                         }
                     }
                }
            }
        },
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        }
    };

    return (
        <div style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
            {/* Header Card */}
            <div
                onMouseEnter={() => setIsHeaderHovered(true)}
                onMouseLeave={() => setIsHeaderHovered(false)}
                style={{
                    backgroundColor: '#fafafa',
                    borderRadius: '8px',
                    padding: '20px 24px 18px',
                    marginBottom: '20px',
                    boxShadow: isHeaderHovered
                        ? '0 4px 12px rgba(0, 0, 0, 0.08)'
                        : '0 1px 3px rgba(0, 0, 0, 0.06)',
                    border: '1px solid rgba(0, 0, 0, 0.06)',
                    transition: 'box-shadow 0.2s ease',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                }}
            >
                {/* Title Block */}
                <div style={{ marginBottom: '12px' }}>
                    {/* Company Name - lightest */}
                    <div style={{
                        fontSize: '11px',
                        color: '#777',
                        fontWeight: 500,
                        lineHeight: 1.2,
                        marginBottom: '1px'
                    }}>
                        {companyName || 'Company'}
                    </div>

                    {/* Main H1 */}
                    <h1 style={{
                        fontSize: '22px',
                        fontWeight: 700,
                        margin: 0,
                        color: '#111',
                        lineHeight: 1.25
                    }}>
                        {selectedProduct ? buildMainTitle() : 'Select a Product'}
                    </h1>

                    {/* Context Line - lighter gray */}
                    {selectedProduct && (
                        <div style={{
                            fontSize: '12px',
                            color: '#999',
                            lineHeight: 1.3,
                            marginTop: '2px'
                        }}>
                            {buildSubline()}
                        </div>
                    )}
                </div>

                {/* Filter Bar */}
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: `${CONTROL_GAP}px`,
                    flexWrap: 'wrap',
                    rowGap: '12px'
                }}>
                    {/* Product */}
                    <div style={{ flex: '1 1 auto', minWidth: 100, maxWidth: 180, boxSizing: 'border-box' }}>
                        <div style={filterLabelStyle}>Product</div>
                        <ProductSelector
                            topicMap={filteredTopicMap}
                            selectedId={selectedProduct || ''}
                            onSelect={setSelectedProduct}
                            label=""
                            size="small"
                            sx={selectStyle}
                        />
                    </div>

                    {/* Time Range */}
                    <div style={{ flex: '1 1 auto', minWidth: 90, maxWidth: 140 }}>
                        <div style={filterLabelStyle}>Time Range</div>
                        <FormControl size="small" sx={selectStyle}>
                            <Select
                                value={timeRange}
                                onChange={(e) => setTimeRange(e.target.value)}
                            >
                                {TIME_RANGE_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    {/* Toggle Reasons Button */}
                    <div style={{ flex: '0 0 auto' }}>
                        <div style={{ ...filterLabelStyle, visibility: 'hidden' }}>&nbsp;</div>
                        <Button
                            variant={showReasons ? 'contained' : 'outlined'}
                            color={showReasons ? 'error' : 'primary'}
                            onClick={() => setShowReasons(!showReasons)}
                            sx={{
                                height: CONTROL_HEIGHT,
                                minWidth: '140px',
                                padding: '6px 16px',
                                fontSize: '13px',
                                fontWeight: 600,
                                textTransform: 'none',
                                boxShadow: 'none',
                                transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
                                '&:hover': {
                                    boxShadow: showReasons
                                        ? '0 2px 6px rgba(211, 47, 47, 0.3)'
                                        : '0 2px 6px rgba(25, 118, 210, 0.3)',
                                }
                            }}
                        >
                            {showReasons ? 'Hide Reasons' : 'Show Reasons'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Chart Section */}
            <div style={{ marginTop: '8px' }}>
                {isLoading ? (
                    <div style={{
                        height: '400px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#666'
                    }}>
                        Loading chart data...
                    </div>
                ) : chartData ? (
                    <div style={{ height: '400px', width: '100%' }}>
                        <Bar data={chartData} options={chartOptions} />
                    </div>
                ) : (
                    <div style={{
                        height: '400px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#666'
                    }}>
                        No data available to display
                    </div>
                )}
            </div>

            {/* Switching Reasons Histogram */}
            {showReasons && selectedProduct && selectedDateRange && (
                <div style={{ marginTop: '20px' }}>
                    <SwitchingReasonsHistogram
                        topicId={selectedProduct}
                        startDate={selectedDateRange.start}
                        endDate={selectedDateRange.end}
                        highlightedPeriod={`${timeRange} period`}
                        style={{
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            padding: '15px',
                            backgroundColor: '#fff'
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default DailyLeaverJoiner;
